import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LorryReceiptReviewService } from '../lorry-receipt-review.service';
import { DoraemonService } from '../doraemon.service';

@Component({
  selector: 'app-lr-details',
  templateUrl: './lr-details.component.html',
  styleUrls: ['./lr-details.component.scss'],
  standalone: false
})
export class LrDetailsComponent implements OnInit {
  accessCode = '';
  isLoading = false;
  errorMessage = '';
  isOtpVerified = false;

  private mobileNumber = '';
  private otp = '';

  lorryReceipt: any = null;
  ewayBills: any[] = [];
  items: any[] = [];
  attachments: any[] = [];
  isEditingReportingTime = false;
  reportingDate: Date | null = null;
  reportingTimeDisplay = '';
  showTimePicker = false;
  currentDate = new Date();
  confirmAction: 'accept' | 'reject' | null = null;
  previewPhotoUrl = '';
  previewPhotoName = '';
  photoUrlMap: Record<string, string> = {};
  photosLoading = false;
  isActing = false;
  actionError = '';
  actionResult: 'APPROVED' | 'REJECTED' | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private lrReviewService: LorryReceiptReviewService,
    private doraemonService: DoraemonService
  ) {
    const navState = this.router.getCurrentNavigation()?.extras?.state as
      | { mobileNumber?: string; otp?: string }
      | undefined;
    if (navState?.mobileNumber && navState?.otp) {
      this.mobileNumber = String(navState.mobileNumber).trim();
      this.otp = String(navState.otp).trim();
    }
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.accessCode = params['a'] || '';
      this.isOtpVerified = false;
      this.lorryReceipt = null;
      this.errorMessage = '';

      if (!this.accessCode) {
        this.isLoading = false;
        this.errorMessage = 'Access code is required';
        return;
      }

      this.resolveAuthCredentials();

      if (!this.mobileNumber || !this.otp || this.otp.length !== 6) {
        this.redirectToVerification();
        return;
      }

      this.loadLorryReceiptAndVerifyOtp();
    });
  }

  get lrNumber(): string {
    return this.lorryReceipt?.lorryReceiptNumber || '-';
  }

  get tspName(): string {
    return this.lorryReceipt?.tspName || '-';
  }

  get tspPan(): string {
    return this.lorryReceipt?.tspPan || '-';
  }

  get truckNumber(): string {
    const value = this.lorryReceipt?.vehicleNumber || '';
    return value ? value.toUpperCase() : '-';
  }

  get fromLocation(): string {
    return this.lorryReceipt?.fromTradeName || this.lorryReceipt?.fromAddress || '-';
  }

  get toLocation(): string {
    return this.lorryReceipt?.toTradeName || this.lorryReceipt?.toAddress || '-';
  }

  get driverName(): string {
    return this.lorryReceipt?.driverName || '-';
  }

  get driverNumber(): string {
    return this.lorryReceipt?.driverNumber || '-';
  }

  get receiverName(): string {
    return this.lorryReceipt?.receiverName || '-';
  }

  get receiverNumber(): string {
    return this.lorryReceipt?.receiverNumber || '-';
  }

  get receiverLocation(): string {
    return this.lorryReceipt?.toTradeName || this.lorryReceipt?.toAddress || '-';
  }

  get consignorName(): string {
    return this.lorryReceipt?.fromTradeName || '-';
  }

  get consignorAddress(): string {
    const address = this.lorryReceipt?.fromAddress || '';
    const gstin = this.lorryReceipt?.fromGstin;
    if (address && gstin) {
      return `${address}\nGSTIN: ${gstin}`;
    }
    return address || (gstin ? `GSTIN: ${gstin}` : '-');
  }

  get consigneeName(): string {
    return this.lorryReceipt?.toTradeName || '-';
  }

  get consigneeAddress(): string {
    const address = this.lorryReceipt?.toAddress || '';
    const gstin = this.lorryReceipt?.toGstin;
    if (address && gstin) {
      return `${address}\nGSTIN: ${gstin}`;
    }
    return address || (gstin ? `GSTIN: ${gstin}` : '-');
  }

  get invoiceNumber(): string {
    const docNos = (this.ewayBills || [])
      .map((bill) => bill?.docNo)
      .filter((value) => !!value);

    return docNos.length ? docNos.join(',\n') : '-';
  }

  get invoiceValue(): string {
    if (!this.ewayBills?.length) {
      return '-';
    }

    const amount = this.ewayBills.reduce((sum, bill) => {
      const value = Number(bill?.totalInvoiceValue);
      return sum + (Number.isNaN(value) ? 0 : value);
    }, 0);

    return `₹ ${amount.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  get reportedTime(): string {
    return this.formatDateTime(this.lorryReceipt?.reportingTime);
  }

  get unloadedTime(): string {
    return this.formatDateTime(this.lorryReceipt?.loadingTime || this.lorryReceipt?.unloadingTime);
  }

  get photoAttachments(): any[] {
    return (this.attachments || []).filter(
      (doc) => doc.type === 'DISPATCH_IMAGE' || this.isImageFile(doc.documentName)
    );
  }

  get isPending(): boolean {
    return (this.lorryReceipt?.status || '').toUpperCase() === 'PENDING' && !this.actionResult;
  }

  get isApproved(): boolean {
    const status = (this.actionResult || this.lorryReceipt?.status || '').toUpperCase();
    return status === 'APPROVED' || status === 'ACCEPTED';
  }

  get isRejected(): boolean {
    const status = (this.actionResult || this.lorryReceipt?.status || '').toUpperCase();
    return status === 'REJECTED';
  }

  startEditReportingTime(): void {
    this.isEditingReportingTime = true;
    this.showTimePicker = false;

    const source = this.lorryReceipt?.reportingTime
      ? new Date(this.lorryReceipt.reportingTime)
      : null;

    if (source && !Number.isNaN(source.getTime())) {
      this.reportingDate = source;
      this.reportingTimeDisplay = this.formatTimeAmPm(source);
    } else {
      this.reportingDate = null;
      this.reportingTimeDisplay = '';
    }
  }

  onReportingDateChange(): void {
    this.reportingTimeDisplay = '';
    this.showTimePicker = false;
  }

  openTimePicker(): void {
    if (!this.reportingDate) {
      return;
    }
    this.showTimePicker = true;
  }

  onReportingTimePicked(event: { hour: number; minute: number; zone: string }): void {
    const hour = event.hour.toString().padStart(2, '0');
    const minute = event.minute.toString().padStart(2, '0');
    this.reportingTimeDisplay = `${hour}:${minute} ${event.zone}`;
    this.showTimePicker = false;
  }

  get editedReportingTimeDisplay(): string {
    if (!this.reportingDate || !this.reportingTimeDisplay) {
      return this.reportedTime;
    }
    return this.formatCombinedReportingTime(this.reportingDate, this.reportingTimeDisplay);
  }

  openConfirm(action: 'accept' | 'reject'): void {
    this.actionError = '';
    this.confirmAction = action;
  }

  closeConfirm(): void {
    if (this.isActing) {
      return;
    }
    this.confirmAction = null;
    this.actionError = '';
  }

  confirmActionSubmit(): void {
    if (!this.confirmAction || !this.accessCode || this.isActing) {
      return;
    }

    const action = this.confirmAction === 'accept' ? 'APPROVE' : 'REJECT';
    const reportingTime = action === 'APPROVE' ? this.buildReportingTimePayload() : undefined;

    this.isActing = true;
    this.actionError = '';

    this.lrReviewService.submitReviewAction(this.accessCode, action, reportingTime).subscribe({
      next: () => {
        this.isActing = false;
        this.confirmAction = null;
        this.actionResult = action === 'APPROVE' ? 'APPROVED' : 'REJECTED';

        if (this.lorryReceipt) {
          this.lorryReceipt = {
            ...this.lorryReceipt,
            status: this.actionResult,
            ...(reportingTime ? { reportingTime } : {})
          };
        }

        this.isEditingReportingTime = false;
        this.showTimePicker = false;
      },
      error: (error) => {
        this.isActing = false;
        this.actionError = this.mapActionError(error);
      }
    });
  }

  get confirmMessage(): string {
    if (this.confirmAction === 'accept') {
      return 'Are you sure you want to Accept this LR?';
    }
    if (this.confirmAction === 'reject') {
      return 'Are you sure you want to Reject this LR?';
    }
    return '';
  }

  private buildReportingTimePayload(): string | undefined {
    if (this.reportingDate && this.reportingTimeDisplay) {
      const date = this.combineDateAndTime(this.reportingDate, this.reportingTimeDisplay);
      if (date) {
        return this.formatApiReportingTime(date);
      }
    }

    if (this.lorryReceipt?.reportingTime) {
      const date = new Date(this.lorryReceipt.reportingTime);
      if (!Number.isNaN(date.getTime())) {
        return this.formatApiReportingTime(date);
      }
    }

    return undefined;
  }

  private combineDateAndTime(dateInput: Date | any, timeDisplay: string): Date | null {
    const match = timeDisplay.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) {
      return null;
    }

    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const zone = match[3].toUpperCase();

    if (zone === 'PM' && hours !== 12) {
      hours += 12;
    }
    if (zone === 'AM' && hours === 12) {
      hours = 0;
    }

    const base = dateInput?.toDate ? dateInput.toDate() : new Date(dateInput);
    if (Number.isNaN(base.getTime())) {
      return null;
    }

    const combined = new Date(base);
    combined.setHours(hours, minutes, 0, 0);
    return combined;
  }

  private formatApiReportingTime(date: Date): string {
    const year = date.getFullYear();
    const month = this.pad(date.getMonth() + 1);
    const day = this.pad(date.getDate());
    const hours = this.pad(date.getHours());
    const minutes = this.pad(date.getMinutes());
    const seconds = this.pad(date.getSeconds());

    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absOffset = Math.abs(offsetMinutes);
    const offsetHours = this.pad(Math.floor(absOffset / 60));
    const offsetMins = this.pad(absOffset % 60);

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000${sign}${offsetHours}${offsetMins}`;
  }

  private mapActionError(error: any): string {
    const apiMessage = error?.error?.error || error?.error?.message;
    if (typeof apiMessage === 'string' && apiMessage.trim()) {
      return apiMessage;
    }
    return 'Failed to submit action. Please try again.';
  }

  getPhotoUrl(attachment: any): string {
    if (!attachment) {
      return '';
    }
    if (attachment.documentUrl) {
      return attachment.documentUrl;
    }
    const cacheKey = this.getAttachmentCacheKey(attachment);
    return this.photoUrlMap[cacheKey] || '';
  }

  openPhoto(photo: any): void {
    const url = this.getPhotoUrl(photo);
    if (!url) {
      this.resolvePhotoUrl(photo, true);
      return;
    }

    this.previewPhotoUrl = url;
    this.previewPhotoName = photo?.documentName || 'Photo';
  }

  closePhotoPreview(): void {
    this.previewPhotoUrl = '';
    this.previewPhotoName = '';
  }

  onPhotoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.style.display = 'none';
    }
  }

  private resolveAuthCredentials(): void {
    if (this.mobileNumber && this.otp) {
      return;
    }

    const historyState = history.state as { mobileNumber?: string; otp?: string } | undefined;
    if (historyState?.mobileNumber && historyState?.otp) {
      this.mobileNumber = String(historyState.mobileNumber).trim();
      this.otp = String(historyState.otp).trim();
      return;
    }

    try {
      const raw = sessionStorage.getItem(this.getAuthStorageKey(this.accessCode));
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      this.mobileNumber = String(parsed?.mobileNumber || '').trim();
      this.otp = String(parsed?.otp || '').trim();
    } catch {
      this.mobileNumber = '';
      this.otp = '';
    }
  }

  private getAuthStorageKey(accessCode: string): string {
    return `lr_review_auth_${accessCode}`;
  }

  private clearAuthCredentials(): void {
    try {
      sessionStorage.removeItem(this.getAuthStorageKey(this.accessCode));
    } catch {
      // no-op
    }
  }

  private redirectToVerification(): void {
    this.clearAuthCredentials();
    this.router.navigate(['/lr'], {
      queryParams: { a: this.accessCode },
      replaceUrl: true
    });
  }

  private loadLorryReceiptAndVerifyOtp(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.isOtpVerified = false;
    this.lorryReceipt = null;
    this.photoUrlMap = {};

    this.lrReviewService.getByAccessCode(this.accessCode).subscribe({
      next: (response) => {
        const data = response?.data || response;
        const lorryReceipt = data?.lorryReceipt || null;

        if (!lorryReceipt?.id) {
          this.isLoading = false;
          this.errorMessage = 'Lorry receipt not found';
          return;
        }

        this.ewayBills = data?.ewayBills || [];
        this.items = data?.items || [];
        this.attachments = data?.attachments || [];

        this.lrReviewService.verifyOtp(this.accessCode, this.mobileNumber, this.otp).subscribe({
          next: () => {
            this.isLoading = false;
            this.isOtpVerified = true;
            this.lorryReceipt = lorryReceipt;
            this.resolvePhotoUrls();
          },
          error: (error) => {
            this.isLoading = false;
            this.isOtpVerified = false;
            this.lorryReceipt = null;
            this.clearAuthCredentials();
            this.errorMessage = this.mapOtpError(error);
            setTimeout(() => this.redirectToVerification(), 1500);
          }
        });
      },
      error: (error) => {
        this.isLoading = false;
        this.isOtpVerified = false;
        this.lorryReceipt = null;
        this.errorMessage = this.mapReviewError(error);
      }
    });
  }

  private mapOtpError(error: any): string {
    const apiMessage = error?.error?.error || error?.error?.message;
    if (typeof apiMessage === 'string' && apiMessage.trim()) {
      return apiMessage;
    }
    return 'Invalid OTP. Please verify again.';
  }

  private resolvePhotoUrls(): void {
    const photos = this.photoAttachments.filter(
      (photo) => photo?.documentId && photo?.documentKey && !photo?.documentUrl
    );

    if (!photos.length) {
      return;
    }

    this.photosLoading = true;

    const requests = photos.map((photo) =>
      this.doraemonService.downloadDocWithS3Url(photo.documentId, photo.documentKey).pipe(
        map((response) => ({
          cacheKey: this.getAttachmentCacheKey(photo),
          documentUrl: response?.documentUrl || ''
        })),
        catchError(() => of({
          cacheKey: this.getAttachmentCacheKey(photo),
          documentUrl: ''
        }))
      )
    );

    forkJoin(requests).subscribe({
      next: (results) => {
        const nextMap = { ...this.photoUrlMap };
        results.forEach((result) => {
          if (result.documentUrl) {
            nextMap[result.cacheKey] = result.documentUrl;
          }
        });
        this.photoUrlMap = nextMap;
        this.photosLoading = false;
      },
      error: () => {
        this.photosLoading = false;
      }
    });
  }

  private resolvePhotoUrl(photo: any, openAfterResolve = false): void {
    if (!photo?.documentId || !photo?.documentKey) {
      return;
    }

    this.doraemonService.downloadDocWithS3Url(photo.documentId, photo.documentKey).subscribe({
      next: (response) => {
        const documentUrl = response?.documentUrl || '';
        if (!documentUrl) {
          return;
        }

        this.photoUrlMap = {
          ...this.photoUrlMap,
          [this.getAttachmentCacheKey(photo)]: documentUrl
        };

        if (openAfterResolve) {
          this.previewPhotoUrl = documentUrl;
          this.previewPhotoName = photo?.documentName || 'Photo';
        }
      }
    });
  }

  private getAttachmentCacheKey(attachment: any): string {
    return `${attachment?.documentId || ''}_${attachment?.documentKey || ''}`;
  }

  private mapReviewError(error: any): string {
    const apiMessage = error?.error?.error || error?.error?.message;
    if (typeof apiMessage === 'string' && apiMessage.trim()) {
      return apiMessage;
    }
    return 'Failed to load lorry receipt';
  }

  private formatDateTime(value: string): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const hours = this.pad(date.getHours());
    const minutes = this.pad(date.getMinutes());
    const day = this.pad(date.getDate());
    const month = this.pad(date.getMonth() + 1);
    const year = date.getFullYear();

    return `${hours}:${minutes}; ${day}/${month}/${year}`;
  }

  private formatTimeAmPm(date: Date): string {
    let hours = date.getHours();
    const minutes = this.pad(date.getMinutes());
    const zone = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) {
      hours = 12;
    }
    return `${this.pad(hours)}:${minutes} ${zone}`;
  }

  private formatCombinedReportingTime(date: Date, timeDisplay: string): string {
    const match = timeDisplay.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) {
      return this.formatDateTime(date.toISOString());
    }

    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const zone = match[3].toUpperCase();

    if (zone === 'PM' && hours !== 12) {
      hours += 12;
    }
    if (zone === 'AM' && hours === 12) {
      hours = 0;
    }

    const day = this.pad(date.getDate());
    const month = this.pad(date.getMonth() + 1);
    const year = date.getFullYear();
    return `${this.pad(hours)}:${this.pad(minutes)}; ${day}/${month}/${year}`;
  }

  private pad(value: number): string {
    return value.toString().padStart(2, '0');
  }

  private isImageFile(filename: string): boolean {
    if (!filename) {
      return false;
    }
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
  }
}
